import React from 'react';
import { AlertTriangle, RefreshCw, ShieldAlert, WifiOff } from 'lucide-react';

/**
 * Global error boundary for the PIE app.
 *
 * Handles three distinct failure modes (previously all showed the generic
 * "Something went wrong" screen, which masked the real cause and made
 * multiple roles/pages feel broken):
 *
 * 1. ChunkLoadError          → silent auto-reload once (lazyWithRetry handles
 *                               the common case; this is the safety net).
 * 2. Forbidden (403)         → "You don't have access" with a Back button.
 * 3. Network / offline       → "Connection issue — check your internet".
 * 4. Everything else         → generic fallback with a Retry and Reload.
 */
const isChunkLoadError = (err) => {
  if (!err) return false;
  const msg = String(err.message || err);
  return (
    err?.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/.test(msg) ||
    /Failed to fetch dynamically imported module/.test(msg) ||
    /Importing a module script failed/.test(msg)
  );
};

const isForbidden = (err) => err?.status === 403;
const isUnauthorized = (err) => err?.status === 401;
const isNetworkError = (err) => {
  if (!err) return false;
  if (err.code === 'network') return true;
  const msg = String(err.message || err);
  return err?.name === 'TypeError' && /Failed to fetch|NetworkError|Load failed/i.test(msg);
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, didReload: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // If it's a chunk-load error, attempt a one-shot recovery reload.
    if (isChunkLoadError(error)) {
      const KEY = 'pie:err-boundary-reloaded-at';
      try {
        const last = parseInt(sessionStorage.getItem(KEY) || '0', 10);
        // Don't reload more than once every 30 seconds
        if (Date.now() - last > 30_000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          if ('caches' in window) {
            caches.keys().then((names) =>
              Promise.all(names.map((n) => caches.delete(n)))
            ).finally(() => window.location.reload());
          } else {
            window.location.reload();
          }
          return;
        }
      } catch (_) { /* fall through to UI */ }
    }
    // Best-effort logging — never crash the error handler itself.
    try {
      console.error('[ErrorBoundary]', error, errorInfo);
    } catch (_) {}
  }

  handleReload = () => {
    try {
      if ('caches' in window) {
        caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
      }
    } catch (_) {}
    window.location.reload();
  };

  handleBack = () => {
    if (window.history.length > 1) window.history.back();
    else window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.error;

    // 1. Chunk-load failure — a reload is already in flight (componentDidCatch)
    //    so show a minimal spinner. If that reload was throttled, fall back
    //    to the generic screen.
    if (isChunkLoadError(err)) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="error-chunk-reloading">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-[#DC2626] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-600 text-sm">Updating to the latest version…</p>
          </div>
        </div>
      );
    }

    // 2. Forbidden — a legit 403 from the backend for this role/endpoint
    if (isForbidden(err) || isUnauthorized(err)) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="error-forbidden">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">You don&apos;t have access to this page</h1>
            <p className="text-slate-600 mb-6">
              Your role ({(JSON.parse(localStorage.getItem('user') || '{}')?.role) || 'guest'}) can&apos;t open this page. If you think this is a mistake, ask an admin to grant access.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleBack}
                className="px-4 py-2 bg-[#DC2626] text-white rounded-lg hover:bg-[#B91C1C]"
                data-testid="err-back-button"
              >
                Back
              </button>
              <button
                onClick={() => (window.location.href = '/')}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                data-testid="err-home-button"
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      );
    }

    // 3. Network error — server unreachable, offline, DNS failure
    if (isNetworkError(err)) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="error-network">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <WifiOff className="w-8 h-8 text-slate-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Connection issue</h1>
            <p className="text-slate-600 mb-6">
              We couldn&apos;t reach the PIE server. Check your internet connection and try again.
            </p>
            <button
              onClick={this.handleReload}
              className="flex items-center gap-2 mx-auto px-4 py-2 bg-[#DC2626] text-white rounded-lg hover:bg-[#B91C1C]"
              data-testid="err-retry-button"
            >
              <RefreshCw size={18} />
              Retry
            </button>
          </div>
        </div>
      );
    }

    // 4. Generic fallback
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="error-generic">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-slate-600 mb-6">
            This page encountered an error. You can go back or reload.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleBack}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
            >
              Back
            </button>
            <button
              onClick={this.handleReload}
              className="flex items-center gap-2 px-4 py-2 bg-[#DC2626] text-white rounded-lg hover:bg-[#B91C1C]"
            >
              <RefreshCw size={18} />
              Reload
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && err && (
            <details className="mt-6 text-left">
              <summary className="text-sm text-slate-500 cursor-pointer">Error details</summary>
              <pre className="mt-2 p-3 bg-slate-100 rounded text-xs overflow-auto max-h-40">
                {err.toString()}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
